import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero, CTASection } from "@/components/site/Sections";
import { serviceIcon } from "@/data/icons";
import { SERVICES } from "@/data/content";

export default function Services() {
  const [openId, setOpenId] = useState(null);
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });
  const list = services.length ? services : SERVICES.map((s, i) => ({ ...s, icon: "building", id: i, details: [] }));

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Our services"
        title="Everything your space needs, under one roof."
        subtitle="Construction, design, renovation, MEP and interiors — an end-to-end offering. Tap a service to see what's included."
        image="https://images.pexels.com/photos/34688219/pexels-photo-34688219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <div className="space-y-px bg-navy/10 border border-navy/10">
          {list.map((sv, i) => {
            const Icon = serviceIcon(sv.icon);
            const open = openId === (sv.id || i);
            const hasDetails = (sv.details || []).length > 0;
            return (
              <div key={sv.id || i} className="bg-cream" data-testid="service-row">
                <button
                  onClick={() => hasDetails && toggle(sv.id || i)}
                  data-testid={`service-toggle-${i}`}
                  className={`group w-full grid md:grid-cols-12 gap-6 p-8 lg:p-10 items-center text-left transition-colors duration-300 ${hasDetails ? "hover:bg-white cursor-pointer" : "cursor-default"}`}
                >
                  <div className="md:col-span-1 font-display text-2xl text-gold">0{i + 1}</div>
                  <div className="md:col-span-1"><Icon className="h-10 w-10 text-navy" strokeWidth={1.2} /></div>
                  <h3 className="md:col-span-4 font-display text-2xl md:text-3xl text-navy">{sv.title}</h3>
                  <p className="md:col-span-5 text-navy/55 leading-relaxed">{sv.description || sv.desc}</p>
                  <div className="md:col-span-1 flex md:justify-end">
                    {hasDetails && (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/20 text-navy group-hover:bg-navy group-hover:text-white transition-colors">
                        {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </span>
                    )}
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {open && hasDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden bg-white"
                      data-testid={`service-details-${i}`}
                    >
                      <div className="px-8 lg:px-10 pb-8 pt-2 md:pl-[calc(16.66%+1.5rem)]">
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {sv.details.map((d) => (
                            <div key={d} className="flex items-center gap-2.5 rounded-sm border border-navy/10 bg-cream px-4 py-3 text-sm text-navy/75">
                              <Check className="h-4 w-4 text-gold shrink-0" /> {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
